import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkingBookingSlice } from '../../../slices/useParkingBookingSlice';
import { useSessionsSlice } from '../../../slices/useSessionsSlice';
import { useTimerSlice } from '../../../slices/useTimerSlice';
import {
  formatTimeElapsed,
  formatTimeRemaining,
} from '../../../utils/utilsTime';
import { SessionTimerProps } from './props';
import { flattenBookings } from '../../../utils/utilsBooking';

const OFFSET_10_MINUTES = 10 * 60 * 1000;

export const SessionTimer = ({}: SessionTimerProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { ongoingSession } = useSessionsSlice();
  const { userBooking } = useParkingBookingSlice();
  const {
    elapsedTime,
    remainingTime,
    isInBufferZone,
    setElapsedTime,
    setRemainingTime,
    setIsInBufferZone,
  } = useTimerSlice();

  const bookingsFromParking = useMemo(
    () => flattenBookings(ongoingSession),
    [ongoingSession],
  );

  const otherUserBookings = useMemo(
    () =>
      bookingsFromParking.filter(
        (booking) => booking.user.id !== ongoingSession?.user.id,
      ),
    [bookingsFromParking, ongoingSession?.user.id],
  );

  const nextUpcomingBookingOnThisGate = useMemo(
    () =>
      (() => {
        if (!ongoingSession?.gate.id) return undefined;
        const nowMs = Date.now();

        // Only consider bookings that start in the future.
        // Otherwise we can incorrectly show "End your booking in 0s" due to stale/past bookings.
        return otherUserBookings
          .filter((booking) => booking?.gate?.id === ongoingSession?.gate?.id)
          .filter((booking) => new Date(booking?.startsAt).getTime() > nowMs)
          .sort(
            (a, b) =>
              new Date(a?.startsAt).getTime() - new Date(b?.startsAt).getTime(),
          )[0];
      })(),
    [otherUserBookings, ongoingSession?.gate.id],
  );

  const handleTimerClick = useCallback(() => {
    if (userBooking?.parking) {
      router.push(`?parkingId=${userBooking.parking.id}`);
      return;
    }

    if (!ongoingSession) return;

    const parkingId =
      ongoingSession.parking.referenceParking?.id ?? ongoingSession.parking.id;
    router.push(`?parkingId=${parkingId}`);
  }, [ongoingSession, router, userBooking]);

  // Update elapsed time for active session
  useEffect(() => {
    if (!ongoingSession?.startedAt || ongoingSession.endedAt) {
      setElapsedTime('');
      return;
    }

    const updateTimer = () => {
      const newElapsedTime = formatTimeElapsed(ongoingSession.startedAt);
      setElapsedTime(newElapsedTime);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => {
      clearInterval(interval);
      setElapsedTime('');
    };
  }, [ongoingSession, setElapsedTime]);

  // Update remaining time for buffer zone or upcoming booking
  useEffect(() => {
    if (nextUpcomingBookingOnThisGate) {
      const updateRemainingTime = () => {
        const bookingStartTime = new Date(
          nextUpcomingBookingOnThisGate.startsAt,
        ).getTime();
        const bufferTime = bookingStartTime - OFFSET_10_MINUTES;
        const now = new Date().getTime();
        const isInBuffer = bufferTime - now <= OFFSET_10_MINUTES;
        const newRemainingTime = formatTimeRemaining(
          new Date(bufferTime).toISOString(),
        );

        setRemainingTime(newRemainingTime);
        setIsInBufferZone(isInBuffer);
      };

      updateRemainingTime();
      const interval = setInterval(updateRemainingTime, 1000);
      return () => {
        clearInterval(interval);
        setRemainingTime('');
        setIsInBufferZone(false);
      };
    }

    if (userBooking && !ongoingSession) {
      const updateUpcomingBooking = () => {
        const newRemainingTime = formatTimeRemaining(userBooking.startsAt);
        setRemainingTime(newRemainingTime);
      };

      updateUpcomingBooking();
      const interval = setInterval(updateUpcomingBooking, 1000);
      return () => {
        clearInterval(interval);
        setRemainingTime('');
      };
    }

    setRemainingTime('');
    setIsInBufferZone(false);
  }, [
    nextUpcomingBookingOnThisGate,
    userBooking,
    ongoingSession,
    setRemainingTime,
    setIsInBufferZone,
  ]);

  const getTimerContent = useCallback(() => {
    // Case 1: User has an upcoming booking
    if (userBooking && !ongoingSession) {
      return {
        label: t('yourBookingStartsIn', {
          parkingName: userBooking?.parking?.name,
        }),
        time: remainingTime,
        isInBufferZone: false,
      };
    }

    // Case 2: User has an active session
    if (ongoingSession) {
      if (nextUpcomingBookingOnThisGate) {
        return {
          label: t('endBookingIn'),
          time: remainingTime,
          isInBufferZone,
        };
      }

      return {
        label: t('activeSessionLabel'),
        time: elapsedTime,
        isInBufferZone: false,
      };
    }

    return null;
  }, [
    t,
    userBooking,
    ongoingSession,
    nextUpcomingBookingOnThisGate,
    remainingTime,
    isInBufferZone,
    elapsedTime,
  ]);

  const content = getTimerContent();
  if (!content) return null;

  const { label, time, isInBufferZone: contentIsInBufferZone } = content;

  return (
    <div
      className={`absolute left-2 right-2 bottom-2 rounded-md shadow-xl text-white p-2 z-[10]  text-center ${
        contentIsInBufferZone ? 'bg-red-500' : 'bg-primary'
      }`}
      onClick={handleTimerClick}
    >
      <span className="text-xs">
        {label} {time}
      </span>
    </div>
  );
};
